/* eslint-disable no-console */
import React, { useCallback, useEffect, useMemo, useState } from 'react';

import { MdCheck } from 'react-icons/md';
import {
  addYears,
  endOfDay,
  format,
  parseISO,
  startOfDay,
  subMonths,
} from 'date-fns';
import { TableList, Container, Filter, Status } from './styles';

import Table, { IColumn } from '~/components/Table';
import FiltersLists from '~/components/FiltersLists';
import ExportCsv from '~/components/ExportCsv';
import api from '~/services/api';
import { formatPrice } from '~/utils/format';
import { IOption } from '~/components/Select';
import Loading from '~/components/Loading';
import QtyRealized from './QtyRealized';
import Sold from './Sold';
import ExportXls from '~/components/ExportXlsx';

interface IDataItem {
  id: number;
  event: string;
  date: string;
  location: string;
  status: string;
  expected_qty: number;
  realized_qty: number;
  total: string;
  received: string;
  active: boolean;
}

interface IItem {
  id: number;
}

interface IOrder {
  processing_fee: string;
  service_fee: string;
  total: string;
  items: IItem[];
}

interface IBatchCategory {
  quantity: number;
  price: string;
}

interface IBatch {
  id: number;
  quantity: number;
  due_date: string;
  batchCategories: IBatchCategory[];
}

interface IEventData {
  id: number;
  name: string;
  date: string;
  slug: string;
  status_id: number;
  status_name: string;
  qty_total: number;
  qty_sold: number;
  street: string;
  number: string;
  neighborhood: string;
  city: string;
  state: string;
  country: string;
  complement: string;
  zip_code: string;
  full_address: string;
  target_revenue: string;
}
interface IEventResponse {
  data: IEventData[];
  from: number;
  to: number;
  total: number;
  pages: number;
}

interface IStatus {
  id: number;
  name: string;
}

const Events: React.FC = () => {
  const [pageSelected, setPageSelected] = useState(1);
  const [data, setData] = useState<IDataItem[]>([]);
  const [tableData, setTableData] = useState({
    from: 0,
    to: 0,
    total: 0,
    totalPage: 0,
    generalTotal: 0,
  });
  const [eventName, setEventName] = useState<string | undefined>(undefined);
  const [statusId, setStatusId] = useState<string | undefined>(undefined);
  const [eventId, setEventId] = useState<string | undefined>(undefined);
  const [startDate, setStartDate] = useState<string | undefined>(undefined);
  const [endDate, setEndDate] = useState<string | undefined>(undefined);
  const [status, setStatus] = useState<IOption[]>([]);
  const [quantity, setQuantity] = useState(10);
  const [loading, setLoading] = useState(false);
  const [ticketsByEvent, setTicketsByEvent] = useState<Record<number, number>>(
    {}
  );

  useEffect(() => {
    api.get<IStatus[]>('events-status').then((response) => {
      const dataStatus = response.data.map<IOption>((statusData) => ({
        id: statusData.id,
        value: statusData.name,
        selected: false,
      }));
      setStatus(dataStatus);
    });
  }, []);

  const handleLoadEvents = useCallback(
    async (
      pageData,
      name?: string,
      status_id?: string,
      dateStart?: string,
      dateEnd?: string,
      quantityData?: number,
      event_id?: string
    ) => {
      setLoading(true);
      const response = await api
        .get<IEventResponse>('events/organizer', {
          params: {
            page: pageData,
            name,
            status_ids: status_id ? [status_id] : undefined,
            startDate: dateStart ? new Date(dateStart) : undefined,
            endDate: dateEnd ? new Date(dateEnd) : undefined,
            isList: true,
            quantity: quantityData,
            event_id,
          },
        })
        .finally(() => {
          setLoading(false);
        });

      const events = response.data.data;

      const ticketsPromises = events.map(async (event) => {
        const { data: responseBatches } = await api.get<IBatch[]>(
          `batches/events/${event.id}`
        );

        const allTickets = responseBatches.reduce(
          (previous, current) => previous + current.quantity,
          0
        );

        return { id: event.id, allTickets };
      });

      const ticketsResult = await Promise.all(ticketsPromises);

      const ticketsMap = ticketsResult.reduce<Record<number, number>>(
        (acc, item) => {
          acc[item.id] = item.allTickets;
          return acc;
        },
        {}
      );

      setTicketsByEvent(ticketsMap);

      const dataAux = events.map<IDataItem>((event) => {
        const location =
          event.full_address ||
          `${event.street} n˚ ${event.number}, ${event.neighborhood} - ${event.city}/${event.state}, CEP: ${event.zip_code}`;

        const received = 0;

        return {
          id: event.id,
          event: event.name,
          date: format(parseISO(event.date), 'dd/MM/yyyy'),
          location,
          status: event.status_name || 'Cadastrando',
          expected_qty: event.qty_total,
          realized_qty: event.qty_sold,
          total: formatPrice(parseFloat(event.target_revenue) || 0),
          received: formatPrice(received),
          active: true,
        };
      });

      setData(dataAux);
      setTableData((oldState) => ({
        from: response.data.from,
        to: response.data.to,
        total: response.data.total,
        totalPage: response.data.pages,
        generalTotal: oldState.generalTotal
          ? oldState.generalTotal
          : response.data.total,
      }));
    },
    []
  );

  useEffect(() => {
    handleLoadEvents(
      1,
      undefined,
      undefined,
      startOfDay(subMonths(new Date(), 6)).toISOString(),
      endOfDay(addYears(new Date(), 1)).toISOString()
    );
  }, [handleLoadEvents]);

  const columns = useMemo<IColumn[]>(
    () => [
      {
        name: 'ID',
        selector: 'id',
        sortable: false,
      },
      {
        name: 'Evento',
        selector: 'event',
        sortable: true,
      },
      {
        name: 'Data',
        selector: 'date',
        sortable: true,
      },
      {
        name: 'Local',
        selector: 'location',
        sortable: true,
      },
      {
        name: 'Status',
        selector: 'status',
        sortable: true,
        cell: (row) => (
          <Status status={row.status} className="text-white rounded-pill mb-0">
            {row.status === 'Esgotado' ? 'Fim vendas' : row.status}
          </Status>
        ),
      },
      {
        name: 'Qtd prevista',
        selector: 'expected_qty',
        sortable: true,
      },
      {
        name: 'Qtd realizada',
        selector: 'realized_qty',
        sortable: true,
        cell: (row) => <QtyRealized eventId={row.id} />,
      },
      {
        name: 'Meta',
        selector: 'total',
        sortable: true,
      },
      {
        name: 'Vendido',
        selector: 'received',
        sortable: true,
        cell: (row) => <Sold eventId={row.id} />,
      },
      // {
      //   name: 'A receber',
      //   selector: 'to_receive',
      //   sortable: true,
      // },
      {
        name: 'Ativo',
        selector: 'active',
        sortable: true,
        cell: () => (
          <button type="button" className="pe-none btn-icons border-0">
            <MdCheck size={16} color="#fff" />
          </button>
        ),
      },
    ],
    []
  );

  const handleChangePage = useCallback(
    (page) => {
      if (page <= tableData.totalPage) {
        handleLoadEvents(
          page,
          eventName,
          statusId,
          startDate,
          endDate,
          quantity,
          eventId
        );
        setPageSelected(page);
      }
    },
    [
      tableData.totalPage,
      handleLoadEvents,
      eventName,
      eventId,
      statusId,
      startDate,
      endDate,
      quantity,
    ]
  );

  const handleSubmitFilters = useCallback(
    (formData) => {
      setEventName(formData.event === 'Todos' ? undefined : formData.event);
      setStatusId(formData.status !== 'todos' ? formData.status : undefined);
      setEventId(formData.event_id === 'Todos' ? undefined : formData.event_id);
      setStartDate(formData.dateStart);
      setEndDate(formData.dateEnd);
      handleLoadEvents(
        1,
        formData.event === 'Todos' ? undefined : formData.event,
        formData.status !== 'todos' ? formData.status : undefined,
        formData.dateStart,
        formData.dateEnd,
        quantity,
        formData.event === 'Todos' ? undefined : formData.event_id
      );
      setPageSelected(1);
    },
    [handleLoadEvents, quantity]
  );

  const handleChangeQuantity = useCallback(
    (quantityData) => {
      handleLoadEvents(
        1,
        eventName,
        statusId,
        startDate,
        endDate,
        quantityData,
        eventId
      );
      setQuantity(quantityData);
      setPageSelected(1);
    },
    [endDate, eventName, handleLoadEvents, startDate, statusId, eventId]
  );

  const handleClickExportCsv = useCallback(
    async (formatExport) => {
      try {
        setLoading(true);

        const { data: events } = await api.get<IEventData[]>(
          'events/organizer',
          {
            params: {
              name: eventName,
              status_ids: statusId ? [statusId] : undefined,
              startDate,
              endDate,
              isList: true,
              event_id: eventId,
            },
          }
        );

        const dataAux = await Promise.all(
          events.map(async (event) => {
            const { data: orders } = await api.get<IOrder[]>(
              `orders/events/${event.id}`
            );

            const location = `${event.street} n˚ ${event.number}, ${event.neighborhood} - ${event.city}/${event.state}, CEP: ${event.zip_code}`;

            const received = orders.reduce((previous, current) => {
              return (
                previous +
                (parseFloat(current.total) -
                  parseFloat(current.processing_fee) -
                  parseFloat(current.service_fee))
              );
            }, 0);

            console.log(event);

            const to_receive = received - received * 0.03;

            return {
              id: event.id,
              event: event.name,
              date: format(parseISO(event.date), 'dd/MM/yyyy'),
              location,
              status:
                event.status_name === 'Esgotado'
                  ? 'Fim vendas'
                  : event.status_name,
              expected_qty: parseInt(event.qty_total.toString(), 10) || 0,
              realized_qty: parseInt(event.qty_sold.toString(), 10) || 0,
              total:
                formatExport === 'csv'
                  ? parseFloat(event.target_revenue || '0.00')
                      .toFixed(2)
                      .toString()
                      .replace('.', ',')
                  : parseFloat(event.target_revenue || '0'),
              received:
                formatExport === 'csv'
                  ? received.toFixed(2).toString().replace('.', ',')
                  : parseFloat(received.toFixed(2).toString()),
            };
          })
        );

        return dataAux;
      } catch (error) {
        console.error('Erro ao exportar CSV', error);
        return [];
      } finally {
        setLoading(false);
      }
    },
    [endDate, eventId, eventName, startDate, statusId]
  );

  return (
    <Container>
      <div className="container-fluid">
        <div className="row">
          <div className="zoom col-12 px-0">
            <div className="bg-white d-sm-flex justify-content-between align-items-center p-4">
              <h1 className="mb-0">Lista de eventos</h1>
              <div>
                <ExportCsv
                  data={data}
                  columns={columns}
                  fileName="Lista_de_eventos"
                  onClick={() => handleClickExportCsv('csv')}
                />
                <ExportXls
                  data={data}
                  columns={columns}
                  fileName="Lista_de_eventos"
                  onClick={() => handleClickExportCsv('xls')}
                  className="export-xls"
                />
              </div>
            </div>
          </div>
          <Filter className="col-12 px-0 mt-3">
            <FiltersLists
              onSubmitFilters={handleSubmitFilters}
              event
              date
              statusEvent
              statusOptions={status}
            />
          </Filter>
          <div className="col-12 px-0">
            <div className="row">
              <TableList className="col-12 mt-3">
                <div className="bg-white pb-2">
                  <div className="bg-white overflow-auto">
                    <div className="">
                      <Table
                        className="mb-0 p-0 table"
                        columns={columns}
                        data={data}
                        pagination
                        changeQuantity
                        showTotals
                        generalTotal={tableData.generalTotal}
                        totalData={tableData.total}
                        selectedPage={pageSelected}
                        onChangePage={handleChangePage}
                        onChangeQuantity={handleChangeQuantity}
                      />
                    </div>
                  </div>
                </div>
              </TableList>
            </div>
          </div>
        </div>
      </div>
      <Loading active={loading} text="Carregando..." />
    </Container>
  );
};

export default Events;
